//@flow
import React, { PureComponent } from 'react';
import { themr } from 'react-css-themr';
import classnames from 'classnames';
import { connect } from 'react-redux';

import UnitConversionHelper from 'common/helpers/UnitConversionHelper';

import { Checkbox } from 'common/components/core/checkbox';
import { translate } from 'react-i18next';
import compose from 'recompose/compose';
import VehicleModelActions from 'common/redux/actions/vehicleModel';
import AppPreferencesActions from 'common/redux/actions/appPreferences';
import VehicleModelMasterImportContainer from 'common/entity/vehicleModelMaster/VehicleModelMasterImportContainer';
import SkillActions from 'common/redux/actions/skill';
import SkillSelectors from 'common/redux/selectors/skill';
import ToastActions from 'common/redux/actions/toast';
import SkillsList from 'common/components/core/common/SkillsList';

import {
    clientSelector,
    isAddEntityActive as isAddEntityActiveSelector,
    isImportEntityActive as isImportEntityActiveSelector,
    isExportEntityActive as isExportEntityActiveSelector,
    editEntityIdSelector
} from 'common/redux/selectors/queryParamsSelectors';
import MasterExportContainer from 'common/entity/generic/MasterExportContainer';
import VehicleModelSelectors from 'common/redux/selectors/vehicleModel';
import {
    getEntitySettings,
    getClientDimensionsMapping
} from 'common/redux/selectors/clientSelector';
import { getEntityTablePreferences } from 'common/redux/selectors/appPreferences';

import { withRouter } from 'common/components/core/router';

import SingleVehicleModelEditContainer from './SingleVehicleModelEditContainer';

import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';

import theme from 'common/entity/BulkEditContainer.scss';
import specificTheme from './VehicleModelMasterContainer.scss';

import { dashboardColors } from 'common/enums/ColorEnum';
import { entityMasterFactory } from 'common/entity/generic/MasterContainer';
import MasterContainerHOC from 'common/entity/generic/MasterContainerHOC';
import {
    ConnectedSkillSelectorFilter,
    ConnectedSkillIdSelectorFilter
} from 'common/entity/components/SkillSelectorFilter';
import GeneralFilter from 'common/entity/components/GeneralFilter';
import FuelTypeFilter from 'common/entity/components/FuelTypeFilter';

import type { MasterVehicleModel } from 'common/models/internal/MasterVehicleModel';
import {
    FuelTypeEnum,
    VehicleTypeEnum,
    FleetTypeEnum,
    LoadingUnloadingSideEnum
} from 'common/models/internal/MasterVehicleModel';

import type { EntitySettings } from 'common/models/internal/EntitySettings';
import type { TFunction } from 'react-i18next';
import type { Skill } from 'common/models/internal/Skill';

import {
    CellDataTypes,
    FROZEN_COLUMN_LIMIT
} from 'common/components/core/table';
import VisitUtils from 'common/utils/VisitUtils';
import { DataUtils } from 'common/utils/DataUtils';

import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import { volumeUnitOptions } from 'common/enums/VolumeUnitEnum';

import type { EntityColumnType } from 'common/entity/generic/MasterContainer';
import {
    ENTITY_TABLE_ID,
    ENTITY_NAME_ID,
    ENTITY_EXPORT_ID
} from 'common/constants/entity/vehicleModelMaster';
import { RoleOperationEnum } from 'common/modules/rbac';
import {
    RESOURCE_PATH,
    CustomColumnHelpers
} from 'common/entity/helpers/CustomColumnHelpers';
import { VehicleCompartmentsDetails } from './VehicleCompartments';
import { PopoverPlaceEnum } from 'common/components/core/popover/popOverFactory';
import { Text } from '@locus-taxy/neo-ui';

const selectSkillsId = ({ id }) => id;

const getLimitCellFn = (type, threshType) => {
    return props => {
        const {
            original,
            tdProps: {
                rest: { unitConverter }
            }
        } = props;

        const { limits: resourceLimits } = original;
        const resource = resourceLimits?.[type];
        if (!DataUtils.isEmpty(resource?.[threshType])) {
            const value = resourceLimits.getPreferredValue({
                value: resource[threshType],
                unitConverter,
                type
            });
            if (type === 'volume') {
                return value;
            }
            const unit = resourceLimits.getPreferredUnitText(
                unitConverter,
                type
            );
            return <span>{`${value} ${unit}`}</span>;
        }
        return <span>{'-'}</span>;
    };
};

const getDimensionCellFn = type => {
    return props => {
        const { original } = props;

        const value = original.getIn(type);
        if (DataUtils.isEmpty(value)) {
            return '-';
        }
        return <span>{`${value} m`}</span>;
    };
};

const getSkillIdFilter = ({ clientId, filteredColumn }) => {
    const {
        value: { allowEmptyOrNull, values }
    } = filteredColumn;
    const skills = values.map(skill => ({
        clientId,
        id: skill
    }));
    return new FilterBuilder()
        .withName('skills')
        .withOperation(FilterOperation.Contains)
        .withValues(skills)
        .withAllowEmptyOrNull(allowEmptyOrNull);
};

const DEFAULT_COLUMNS: Array<EntityColumnType> = [
    {
        id: 'selector',
        Cell: props => {
            const {
                tdProps: {
                    rest: { isChecked, onCheckboxClick }
                }
            } = props;
            return (
                <Checkbox
                    checkboxColor={dashboardColors.darkSkyBlue.name}
                    className={theme.selectionCell}
                    theme={theme}
                    checked={isChecked}
                    onChange={onCheckboxClick}
                />
            );
        },
        sortable: false,
        filterable: false,
        frozen: true,
        disablePinControl: true,
        width: 40
    },
    {
        headerTextId: 'vehicleModelTable.id',
        accessor: 'id',
        minWidth: 100,
        infixSearchable: true,
        frozen: true
    },
    {
        headerTextId: 'vehicleModelTable.code',
        accessor: 'code',
        frozen: true,
        infixSearchable: true,
        show: false
    },
    {
        headerTextId: 'vehicleModelTable.name',
        accessor: 'name',
        infixSearchable: true
    },
    {
        headerTextId: 'vehicleModelTable.description',
        accessor: 'description',
        sortable: false,
        infixSearchable: true
    },
    {
        headerTextId: 'vehicleModelTable.vehicleType',
        accessor: 'vehicleType',
        FilterComponent: FuelTypeFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('vehicleType')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            return VehicleTypeEnum.getOptions({
                translationKey: 'masterVehicleModelProperties.vehicleTypes'
            });
        }
    },
    {
        headerTextId: 'vehicleModelTable.allowMultiTrip',
        accessor: 'allowMultiTrip',
        sortable: false,
        minWidth: 100,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { t }
                }
            } = props;
            const { allowMultiTrip } = original;
            return allowMultiTrip
                ? t('vehicleModelTable.boolean.yes')
                : t('vehicleModelTable.boolean.no');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { t } = column.getHeaderProps();
            const optionsForSelect = [
                { value: true, label: t('vehicleModelTable.boolean.yes') },
                { value: false, label: t('vehicleModelTable.boolean.no') }
            ];
            return optionsForSelect;
        }
    },
    {
        headerTextId: 'vehicleModelTable.limitMultiTrip',
        accessor: 'limitMultiTrip',
        sortable: false,
        minWidth: 100,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { t }
                }
            } = props;
            const { limitMultiTrip } = original;
            return limitMultiTrip
                ? t('vehicleModelTable.boolean.yes')
                : t('vehicleModelTable.boolean.no');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { t } = column.getHeaderProps();
            const optionsForSelect = [
                { value: true, label: t('vehicleModelTable.boolean.yes') },
                { value: false, label: t('vehicleModelTable.boolean.no') }
            ];
            return optionsForSelect;
        }
    },
    {
        headerTextId: 'vehicleModelTable.maxAdditionalTrips',
        accessor: 'maxAdditionalTrips',
        id: 'maxAdditionalTrips',
        sortable: true,
        dataType: CellDataTypes.Number,
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.loadingParams.unloadingDuration',
        property: 'loadingParams.unloadingDuration',
        id: 'loadingParams.unloadingDuration',
        minWidth: 100,
        dataType: CellDataTypes.HumanizedTime,
        accessor: 'loadingParams.unloadingDuration',
        timeUnits: ['h', 'm', 's']
    },
    {
        headerTextId: 'vehicleModelTable.loadingParams.loadingDuration',
        id: 'loadingParams.loadingDuration',
        property: 'loadingParams.loadingDuration',
        minWidth: 100,
        dataType: CellDataTypes.HumanizedTime,
        accessor: 'loadingParams.loadingDuration',
        timeUnits: ['h', 'm', 's']
    },
    {
        headerTextId: 'vehicleModelTable.limits.minAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.km'),
            limit: t('masterVehicleModelProperties.limits.distance')
        }),
        id: 'minDistance',
        fieldId: 'limits.distance.lowerThresh',
        property: 'limits.distance.lowerThresh',
        minWidth: 100,
        Cell: getLimitCellFn('distance', 'lowerThresh')
    },
    {
        headerTextId: 'vehicleModelTable.limits.maxAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.km'),
            limit: t('masterVehicleModelProperties.limits.distance')
        }),
        id: 'maxDistance',
        fieldId: 'limits.distance.higherThresh',
        property: 'limits.distance.higherThresh',
        Cell: getLimitCellFn('distance', 'higherThresh'),
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.limits.minAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.kg'),
            limit: t('masterVehicleModelProperties.limits.weight')
        }),
        id: 'minWeight',
        fieldId: 'limits.weight.lowerThresh',
        property: 'limits.weight.lowerThresh',
        Cell: getLimitCellFn('weight', 'lowerThresh'),
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.limits.maxAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.kg'),
            limit: t('masterVehicleModelProperties.limits.weight')
        }),
        id: 'maxWeight',
        fieldId: 'limits.weight.higherThresh',
        property: 'limits.weight.higherThresh',
        Cell: getLimitCellFn('weight', 'higherThresh'),
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.limits.minAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.minutes'),
            limit: t('masterVehicleModelProperties.limits.travelTime')
        }),
        id: 'minTravelTime',
        fieldId: 'limits.travelTime.lowerThresh',
        property: 'limits.travelTime.lowerThresh',
        Cell: getLimitCellFn('travelTime', 'lowerThresh'),
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.limits.maxAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.minutes'),
            limit: t('masterVehicleModelProperties.limits.travelTime')
        }),
        id: 'maxTravelTime',
        fieldId: 'limits.travelTime.higherThresh',
        property: 'limits.travelTime.higherThresh',
        Cell: getLimitCellFn('travelTime', 'higherThresh'),
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.limits.minAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.minutes'),
            limit: t('masterVehicleModelProperties.limits.totalTime')
        }),
        id: 'minTotalTime',
        fieldId: 'limits.totalTime.lowerThresh',
        property: 'limits.totalTime.lowerThresh',
        Cell: getLimitCellFn('totalTime', 'lowerThresh'),
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.limits.maxAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.minutes'),
            limit: t('masterVehicleModelProperties.limits.totalTime')
        }),
        id: 'maxTotalTime',
        fieldId: 'limits.totalTime.higherThresh',
        property: 'limits.totalTime.higherThresh',
        Cell: getLimitCellFn('totalTime', 'higherThresh'),
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.limits.minAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.count'),
            limit: t('masterVehicleModelProperties.limits.location')
        }),
        id: 'minLocation',
        fieldId: 'limits.location.lowerThresh',
        property: 'limits.location.lowerThresh',
        accessor: 'limits.location.lowerThresh',
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.limits.maxAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.count'),
            limit: t('masterVehicleModelProperties.limits.location')
        }),
        id: 'maxLocation',
        fieldId: 'limits.location.higherThresh',
        property: 'limits.location.higherThresh',
        accessor: 'limits.location.higherThresh',
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.limits.minAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.count'),
            limit: t('masterVehicleModelProperties.limits.task')
        }),
        id: 'minTask',
        accessor: 'limits.task.lowerThresh',
        fieldId: 'limits.task.lowerThresh',
        property: 'limits.task.lowerThresh',
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.limits.maxAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.count'),
            limit: t('masterVehicleModelProperties.limits.task')
        }),
        id: 'maxTask',
        accessor: 'limits.task.higherThresh',
        fieldId: 'limits.task.higherThresh',
        property: 'limits.task.higherThresh',
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.limits.minAllowedLimitWithoutUnits',
        translationProps: ({ t }) => {
            return {
                limit: t('masterVehicleModelProperties.limits.volume')
            };
        },
        id: 'minVolume',
        fieldId: 'limits.volume.lowerThresh',
        property: 'limits.volume.lowerThresh',
        Cell: getLimitCellFn('volume', 'lowerThresh'),
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.limits.maxAllowedLimitWithoutUnits',
        translationProps: ({ t }) => {
            return {
                limit: t('masterVehicleModelProperties.limits.volume')
            };
        },
        id: 'maxVolume',
        fieldId: 'limits.volume.higherThresh',
        property: 'limits.volume.higherThresh',
        Cell: getLimitCellFn('volume', 'higherThresh'),
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.limits.transactionTimePerTask',
        translationProps: ({ t }) => ({
            unit: t('translations:units.minutes')
        }),
        id: 'transactionTimePerTask',
        fieldId: 'limits.transactionTimePerTask',
        property: 'limits.transactionTimePerTask',
        dataType: CellDataTypes.HumanizedTime,
        accessor: 'limits.transactionTimePerTask',
        timeUnits: ['h', 'm', 's'],
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.skillId',
        id: 'skills.id',
        exportId: 'skills.id',
        sortable: false,
        accessor: 'skills',
        FilterComponent: ConnectedSkillIdSelectorFilter,
        getCustomFilterInfo: getSkillIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { skills }
                }
            } = props;
            const skillIds = (original: MasterLocation).skills;
            return (
                <SkillsList
                    skills={skills}
                    skillIds={skillIds}
                    labelSelector={selectSkillsId}
                />
            );
        }
    },
    {
        headerTextId: 'vehicleModelTable.skillName',
        id: 'skills.name',
        exportId: 'skills.name',
        sortable: false,
        accessor: 'skills',
        FilterComponent: ConnectedSkillSelectorFilter,
        getCustomFilterInfo: getSkillIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { skills }
                }
            } = props;
            const skillIds = (original: MasterLocation).skills;
            return <SkillsList skills={skills} skillIds={skillIds} />;
        }
    },
    {
        accessor: 'maxPayloadWeight',
        headerTextId: 'vehicleModelTable.maxPayloadWeight',
        Cell: props => {
            const { original } = props;
            const value = original.getIn(['maxPayloadWeight']);
            if (DataUtils.isEmpty(value)) {
                return '-';
            }
            return <span>{value} Kg</span>;
        }
    },
    {
        headerTextId: 'vehicleModelTable.isColdChain',
        accessor: 'isColdChain',
        minWidth: 100,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { t }
                }
            } = props;
            const { isColdChain } = original;
            return isColdChain
                ? t('vehicleModelTable.boolean.yes')
                : t('vehicleModelTable.boolean.no');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { t } = column.getHeaderProps();
            const optionsForSelect = [
                { value: true, label: t('vehicleModelTable.boolean.yes') },
                { value: false, label: t('vehicleModelTable.boolean.no') }
            ];
            return optionsForSelect;
        }
    },
    {
        headerTextId: 'vehicleModelTable.isDeepFreeze',
        accessor: 'isDeepFreeze',
        sortable: false,
        minWidth: 100,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { t }
                }
            } = props;
            const { isDeepFreeze } = original;
            return isDeepFreeze
                ? t('vehicleModelTable.boolean.yes')
                : t('vehicleModelTable.boolean.no');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { t } = column.getHeaderProps();
            const optionsForSelect = [
                { value: true, label: t('vehicleModelTable.boolean.yes') },
                { value: false, label: t('vehicleModelTable.boolean.no') }
            ];
            return optionsForSelect;
        }
    },
    {
        headerTextId: 'vehicleModelTable.fuelType',
        accessor: 'fuelType',
        FilterComponent: FuelTypeFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('fuelType')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = FuelTypeEnum.getOptions({
                translationKey: 'masterVehicleModelProperties.fuelTypes'
            });
            return options;
        }
    },
    {
        headerTextId: 'vehicleModelTable.fleetType',
        accessor: 'fleetType',
        sortable: false,
        minWidth: 100,
        FilterComponent: FuelTypeFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('fleetType')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = FleetTypeEnum.getOptions({
                translationKey: 'masterVehicleModelProperties.fleetTypesOptions'
            });
            return options;
        },
        Cell: props => {
            const {
                original: { fleetType },
                tdProps: {
                    rest: { t }
                }
            } = props;
            return t(
                `masterVehicleModelProperties.fleetTypesOptions.${fleetType}`
            );
        }
    },
    {
        headerTextId: 'vehicleModelTable.transporterId',
        accessor: 'transporterId.transporterId',
        exportId: 'transporterId.transporterId',
        minWidth: 100,
        sortable: false,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const { value } = filteredColumn;
            return new FilterBuilder()
                .withName('transporterId.transporterId')
                .withOperation(FilterOperation.ContainsToken)
                .withValue(value);
        }
    },
    {
        headerTextId: 'vehicleModelTable.loadingUnloadingSide',
        accessor: 'unloadingSide',
        exportId: 'loadingUnloadingSide',
        FilterComponent: FuelTypeFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('unloadingSide')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = LoadingUnloadingSideEnum.getOptions({
                translationKey:
                    'masterVehicleModelProperties.loadingUnloadingSides'
            });
            return options;
        }
    },
    {
        headerTextId: 'vehicleModelTable.doorWidth',
        accessor: 'doorWidth',
        Cell: getDimensionCellFn(['doorWidth']),
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.doorHeight',
        accessor: 'doorHeight',
        Cell: getDimensionCellFn(['doorHeight']),
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.dimensions.height',
        accessor: 'dimensions.height',
        Cell: getDimensionCellFn(['dimensions', 'height']),
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.dimensions.breadth',
        accessor: 'dimensions.breadth',
        Cell: getDimensionCellFn(['dimensions', 'breadth']),
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.dimensions.length',
        accessor: 'dimensions.length',
        Cell: getDimensionCellFn(['dimensions', 'length']),
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.containerDimensions.length',
        accessor: 'containerDimensions.length',
        Cell: getDimensionCellFn(['containerDimensions', 'length']),
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.containerDimensions.breadth',
        accessor: 'containerDimensions.breadth',
        Cell: getDimensionCellFn(['containerDimensions', 'breadth']),
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.containerDimensions.height',
        accessor: 'containerDimensions.height',
        Cell: getDimensionCellFn(['containerDimensions', 'height']),
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.volumeUnit',
        accessor: 'volume.unit',
        FilterComponent: FuelTypeFilter,
        Cell: ({
            original: vehicleModel,
            tdProps: {
                rest: { t }
            }
        }) => {
            if (vehicleModel.volume.unit) {
                return t(
                    `translations:volumeUnits.${vehicleModel.volume.unit}`
                );
            }
            return null;
        },
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('volume.unit')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => volumeUnitOptions
    },
    {
        headerTextId: 'vehicleModelTable.compartments',
        accessor: 'compartments',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { t, theme, unitConverter }
                }
            } = props;

            const { compartments } = original;

            return (
                <VehicleCompartmentsDetails
                    t={t}
                    list={compartments}
                    theme={theme}
                    listTitle="Compartments"
                    ripple={false}
                    preferPlace={PopoverPlaceEnum.Left}
                    unitConverter={unitConverter}
                    popOverClassName={theme.compartmentsPopover}
                >
                    <Text color="rgba(52,57,71,.8)" fontSize={0}>
                        {t(
                            'masterVehicleModelProperties.compartments.compartmentWithCount',
                            { count: compartments.size }
                        )}
                    </Text>
                </VehicleCompartmentsDetails>
            );
        },
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.volume',
        accessor: 'volume',
        Cell: props => {
            const { original } = props;

            const value = original.getIn(['volume', 'value']);
            if (DataUtils.isEmpty(value)) {
                return '-';
            }
            return value;
        },
        minWidth: 100
    },
    {
        accessor: 'unladenWeight',
        headerTextId: 'vehicleModelTable.unladenWeight',
        Cell: props => {
            const { original } = props;

            const value = original.getIn(['unladenWeight']);
            if (DataUtils.isEmpty(value)) {
                return '-';
            }
            return <span>{`${value} Kg`}</span>;
        },
        minWidth: 100
    },
    {
        accessor: 'travelParams.maxSpeed',
        headerTextId: 'vehicleModelTable.travelParams.maxSpeed',
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.userCount',
        accessor: 'userCount',
        minWidth: 100
    },
    {
        headerTextId: 'vehicleModelTable.updatedBy',
        id: 'updatedBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.updatedBy',
        Cell: props => {
            const {
                original: vehicleModel,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const updatedBy = VisitUtils.formatIDs(
                vehicleModel?.auditMetadata?.updatedBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={updatedBy} className={theme.cellText}>
                    {updatedBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'vehicleModelTable.updatedOn',
        accessor: 'auditMetadata.updatedOn',
        id: 'updatedOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'vehicleModelTable.createdBy',
        id: 'createdBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.createdBy',
        Cell: props => {
            const {
                original: vehicleModel,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const createdBy = VisitUtils.formatIDs(
                vehicleModel?.auditMetadata?.createdBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={createdBy} className={theme.cellText}>
                    {createdBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'vehicleModelTable.createdOn',
        accessor: 'auditMetadata.createdOn',
        id: 'createdOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    }
];

type PaginationInfoDataType = {
    currentPage: number,
    numberOfPages: number,
    total: number
};

type VehicleModelsDataType = {
    vehicleModels: Array<MasterVehicleModel>,
    paginationInfo: PaginationInfoDataType
};

type Props = {
    clientId: string,
    theme: Object,
    data: VehicleModelsDataType,
    entitySettings: ?EntitySettings,
    tableSettings: ?Array<Object>,
    t: TFunction,
    skills: Array<Skill>,
    fetchSkills: ({ clientId: string }) => void,
    fetchOptions: () => {}
};

const vehicleModelMasterContainerFactory = ({
    EntityEditViewContainer = SingleVehicleModelEditContainer,
    EntityImportContainer = VehicleModelMasterImportContainer,
    COLUMNS = DEFAULT_COLUMNS,
    options = {
        entityTableId: 'VehicleModelMaster',
        operations: {
            edit: RoleOperationEnum.UPDATE_VEHICLE_MODEL,
            add: RoleOperationEnum.UPDATE_VEHICLE_MODEL,
            export: RoleOperationEnum.EXPORT_VEHICLE_MODEL
        }
    },
    ActionsBar,
    BulkViewEmptyState,
    fetchEntities = VehicleModelActions.fetchActiveEntities
}) => {
    const VehicleModelMaster = entityMasterFactory({
        EntityEditViewContainer,
        EntityImportContainer,
        EntityExportContainer: MasterExportContainer,
        COLUMNS,
        options,
        ActionsBar,
        BulkViewEmptyState
    });
    class VehicleModelMasterContainer extends PureComponent<Props> {
        componentDidMount = () => {
            this.fetchOtherResources();
        };

        componentDidUpdate = (prevProps: Props) => {
            const { clientId } = this.props;
            if (clientId !== prevProps.clientId) {
                this.fetchOtherResources();
            }
        };

        fetchOtherResources = () => {
            const { clientId, fetchSkills } = this.props;
            fetchSkills({ clientId });
        };

        getTdProps = (state, rowInfo) => {
            if (!rowInfo) {
                return {};
            }
            const {
                original: { id }
            } = rowInfo;
            const {
                t,
                entitySettings,
                selectedRows,
                toggleRowSelection,
                theme,
                isGlobalPersonnel,
                unitConverter,
                skills
            } = this.props;
            const isChecked = selectedRows.indexOf(id) >= 0;
            return {
                onCheckboxClick: () => {
                    toggleRowSelection(id);
                },
                t,
                entitySettings,
                theme,
                isChecked,
                skills,
                isGlobalPersonnel,
                unitConverter,
                className: classnames({ [theme.selectedRow]: isChecked })
            };
        };

        render() {
            const { clientId, fetchOptions, t, entitySettings } = this.props;
            const vehicleModelFetchOptions = {
                clientId,
                ...(fetchOptions || {})
            };
            const customResourceLimitsColumns = entitySettings
                ? CustomColumnHelpers.getCustomResourceColumns(
                      entitySettings,
                      'vehicleModelSettings',
                      RESOURCE_PATH
                  )
                : [];
            return (
                <VehicleModelMaster
                    {...this.props}
                    emptyStateImageSrc="/img/vehicleModel-empty.png"
                    entityNameId={ENTITY_NAME_ID}
                    entityExportId={ENTITY_EXPORT_ID}
                    entityId={'vehiclemodelId'}
                    entitySettingsName={'vehicleModelSettings'}
                    fetchOptions={vehicleModelFetchOptions}
                    getTdProps={this.getTdProps}
                    hasFreezableColumns
                    frozenColumnLimit={FROZEN_COLUMN_LIMIT}
                    pinTooltip={t('pinTooltip')}
                    unpinTooltip={t('unpinTooltip')}
                    enableCascadeWarning
                    customResourceLimitsColumns={customResourceLimitsColumns}
                />
            );
        }
    }

    const mapStateToProps = (state, ownProps) => {
        const clientId = clientSelector(state, ownProps);
        const isLoading = VehicleModelSelectors.isEntitiesLoading(
            state,
            ownProps
        );
        const data = VehicleModelSelectors.getEntities(state, ownProps);
        const isAddEntityActive = isAddEntityActiveSelector(state, ownProps);
        const entityIdToEdit = editEntityIdSelector(state, ownProps);
        const entitySettings = getEntitySettings(state, ownProps);
        const tableSettings = getEntityTablePreferences(state, ENTITY_TABLE_ID);
        const isImportEntityActive = isImportEntityActiveSelector(
            state,
            ownProps
        );
        const skills = SkillSelectors.getEntities(state, ownProps);
        const isExportEntityActive = isExportEntityActiveSelector(
            state,
            ownProps
        );
        const isGlobalPersonnel = isGlobalPersonnelSelector(state, ownProps);

        const dimensionsMapping = getClientDimensionsMapping(state, ownProps);

        let unitConverter = null;
        if (dimensionsMapping) {
            unitConverter = new UnitConversionHelper({
                dimensionsMapping
            });
        }

        return {
            clientId,
            isLoading,
            data,
            isAddEntityActive,
            entityIdToEdit,
            entitySettings,
            tableSettings,
            isImportEntityActive,
            skills,
            isExportEntityActive,
            isGlobalPersonnel,
            unitConverter
        };
    };

    const withRedux = connect(mapStateToProps, {
        fetchEntities,
        showToastAction: ToastActions.showToast,
        updateToastAction: ToastActions.updateToast,
        hideToastAction: ToastActions.hideToast,
        fetchSkills: SkillActions.fetchEntities,
        disableEntity: VehicleModelActions.disableEntity,
        clearData: VehicleModelActions.clearData,
        saveTableSettings: AppPreferencesActions.saveEntityTablePreferences,
        bulkDisableEntity: VehicleModelActions.bulkDisableEntity
    });

    const enhance = compose(
        themr(null, theme),
        themr(null, specificTheme),
        withRouter,
        withRedux,
        translate('entityManagement'),
        MasterContainerHOC
    );
    return enhance(VehicleModelMasterContainer);
};

const ThemedConnectedVehicleModelMasterContainer = vehicleModelMasterContainerFactory(
    {}
);

export {
    vehicleModelMasterContainerFactory,
    ThemedConnectedVehicleModelMasterContainer as VehicleModelMasterContainer
};
export default ThemedConnectedVehicleModelMasterContainer;
